import React from 'react';
import { inject } from 'mobx-react';
import PropTypes from 'prop-types';
import { SVGIcon } from 'common/components';
import { getHours, renderDates, getHighlightedDates } from './utils/utils';
import EventDetailsBreadcrumb from './components/EventDetailsBreadcrumb';
import { useClassName, useWindowSize } from 'common/hooks';
import { Loader } from 'common/lazy';
import { ContentLink } from 'common/components';

import {
  useEventBlueprint,
  useFeaturedEvents,
  useEventDetails,
  useShare,
  useEvents,
  useImageSelection,
} from './hooks';
import CTAButton from './components/CTAButton';
import FeaturedEvents from './components/FeaturedEvents';

import { ContentfulRichText } from 'common/components';
import './ad-event-details.less';

const AdEventDetail = (props) => {
  const className = useClassName('AdEventDetails');
  const { isMobile } = useWindowSize();
  const { slug } = props.match.params;
  const { getImageComponent } = useImageSelection();

  const blueprint = useEventBlueprint(props.adEvents);
  const { events } = useEvents(props.adEvents);
  const { featuredEvents } = useFeaturedEvents({
    blueprint,
    events,
    excludeSlug: slug,
    fillFeatured: true,
  });

  const { event, isLoading } = useEventDetails(slug, props.adEvents);
  const { share, shareSnackbar } = useShare(event?.slug);

  if (isLoading || !event || !featuredEvents) {
    return (
      <div className={className('loader')}>
        <Loader inline="centered" active />
      </div>
    );
  }

  const renderCTAs = () => {
    if (!event.type || !event.url) return null;
    return (
      <CTAButton text={event.type} link={event.url} variant="primary-card" />
    );
  };

  const canShare = () => Boolean(navigator?.clipboard?.writeText);

  const highlightedDates = getHighlightedDates(event);

  const renderHighlightedDate = (highlightedDate, index) => {
    const { title, showTitle, date, time, overline, hasOverline } =
      highlightedDate;

    return (
      <div
        key={index}
        className={className('details-highlighted-date-section')}>
        {hasOverline && (
          <div className={className('details-highlighted-dates-overline')}>
            {overline}
          </div>
        )}
        <div className={className('details-times-text')}>
          {showTitle ? title : date}
        </div>
        {time.map((time, index) => (
          <div
            key={index}
            className={className('details-highlighted-dates-time')}>
            {time}
          </div>
        ))}
      </div>
    );
  };

  return (
    // @ts-ignore
    <>
      {shareSnackbar}

      <div className={className('details-page-wrapper')}>
        <div className={className('details-img')}>
          {getImageComponent({
            mobileImage: event.mobileImage,
            defaultImage: event.images,
            className: className('image'),
          })}
        </div>
        <div className={className('breadcrumbs')}>
          <EventDetailsBreadcrumb
            type={{
              name: event.venue?.name || '',
              slug: event.venue?.slug || '',
            }}
            event={event}
          />
        </div>
        <div className={className('details-wrapper')}>
          {canShare() && (
            <div className={className('details-title-and-share')}>
              <div className={className('details-title')}>
                {event && event.title}
              </div>
              <div className={className('details-share')} onClick={share}>
                <SVGIcon name="share-arrow" size="tiny" />
                <div className={className('details-share-text')}>
                  Share event
                </div>
              </div>
            </div>
          )}
          <div className={className('details-description-and-dates')}>
            <div className={className('details-description-and-ctas')}>
              <div className={className('details-description')}>
                <ContentfulRichText field={event.details} />
              </div>
              {!isMobile && (
                <div className={className('details-ctas')}>{renderCTAs()}</div>
              )}
            </div>
            <div className={className('details-dates-times-location-parking')}>
              <div className={className('details-dates')}>
                <SVGIcon name="event-details-calendar" size="tiny" />
                {renderDates(event)}
              </div>
              <div className={className('details-times')}>
                <SVGIcon name="event-details-clock" size="tiny" />
                <div className={className('details-times-text-container')}>
                  {getHours(event).map((hour) => (
                    <div key={hour}>{hour}</div>
                  ))}
                  {highlightedDates.length > 0 && (
                    <div
                      className={className(
                        'details-highlighted-dates-container'
                      )}>
                      {highlightedDates.map((highlightedDate, index) =>
                        renderHighlightedDate(highlightedDate, index)
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className={className('details-location')}>
                <SVGIcon name="event-details-map" size="tiny" />
                {event.venue && event.venue.name}
                {!event.venue && event.locationDescription}
              </div>
              <div className={className('details-parking')}>
                <SVGIcon name="parking-square" size="tiny" />
                <ContentLink
                  href="/parking/tickets"
                  style={{ textDecoration: 'underline' }}>
                  Get Parking Tickets
                </ContentLink>
              </div>
            </div>
            {isMobile && (
              <div className={className('details-ctas')}>{renderCTAs()}</div>
            )}
          </div>
        </div>
        {featuredEvents.length > 0 && (
          <div className={className('featured-events-section')}>
            <FeaturedEvents
              blueprint={blueprint}
              events={featuredEvents}
              inDetail={true}
            />
          </div>
        )}
      </div>
    </>
  );
};

AdEventDetail.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  adEvents: PropTypes.shape({
    getAllEvents: PropTypes.func.isRequired,
    getEventBySlug: PropTypes.func.isRequired,
    getEventsBlueprint: PropTypes.func.isRequired,
  }).isRequired,
};

export default inject('adEvents')(AdEventDetail);
